<template>
	<div style="text-align: left">
		<h2>Integration Settings</h2>
		<p style="color: #2c3e50">Update this integrations settings</p>
		<el-tag @click.native="copy()" style="cursor: pointer" type="info">
			Integration ID: {{ integration.id }}
		</el-tag>
		<el-divider></el-divider>
		<el-form
			ref="form"
			@keyup.native.enter="update()"
			:rules="formRules"
			label-position="top"
			label-width="100px"
			:model="form"
		>
			<el-form-item prop="displayName" label="Display Name">
				<el-input
					:show-word-limit="true"
					maxlength="50"
					resize="none"
					type="text"
					placeholder=""
					v-model="form.displayName"
				>
				</el-input>
			</el-form-item>
			<el-form-item prop="description" label="Description">
				<el-input
					:show-word-limit="true"
					maxlength="150"
					resize="none"
					type="textarea"
					:autosize="{ minRows: 3, maxRows: 9 }"
					placeholder="..."
					v-model="form.description"
				>
				</el-input>
			</el-form-item>
			<el-form-item prop="continueOnFail" label="Continue On Fail">
				<el-switch
					style="margin-right: 20px"
					v-model="form.continueOnFail"
					active-text=""
					:inactive-text="form.continueOnFail ? 'On' : 'Off'"
				>
				</el-switch>
			</el-form-item>
			<el-form-item prop="doNotCombineInputAndOutputData" label="Do Not Combine Input & Output Data">
				<el-switch
					style="margin-right: 20px"
					v-model="form.doNotCombineInputAndOutputData"
					active-text=""
					:inactive-text="form.doNotCombineInputAndOutputData ? 'On' : 'Off'"
				>
				</el-switch>
			</el-form-item>
			<el-form-item prop="optionalOutputKey" label="Optional Outgoing Data Key">
				<el-input
					:show-word-limit="true"
					maxlength="70"
					resize="none"
					type="text"
					placeholder=""
					v-model="form.optionalOutputKey"
				>
				</el-input>
				<p class="fieldHelper">
					Useful for name spacing data when using multiple integrations of the same type in sequence
				</p>
			</el-form-item>
			<el-button @click="form.mode ? (form.mode = null) : (form.mode = 'blocking')" type="text">
				{{
					!form.mode
						? "Specify execution mode at integration level"
						: "Delegate execution mode to workflow settings"
				}}
			</el-button>
			<el-form-item v-if="form.mode" prop="mode" label="Execution Mode">
				<el-radio-group v-model="form.mode">
					<el-radio-button label="blocking"></el-radio-button>
					<el-radio-button label="sequential"></el-radio-button>
				</el-radio-group>
				<p class="fieldHelper">
					About mode:
					{{
						form.mode === "blocking"
							? "Blocking mode restricts this integration from executing before all integrations that connect to it have executed"
							: "Sequential mode allows the integration to execute as soon as any data has been passed to it from any other integration"
					}}
				</p>
			</el-form-item>
			<el-form-item>
				<el-button
					@click="update()"
					:disabled="false"
					style="width: 100%; margin-top: 12px"
					type="primary"
					:loading="operationInProgress"
					>Update</el-button
				>
			</el-form-item>
		</el-form>
	</div>
</template>

<script>
import { copyToClipboard } from "../utils/copyToClipboard";
import { mapActions } from "vuex";
import { v4 as uuidv4 } from "uuid";
export default {
	props: {
		integration: Object,
		workflow: Object,
	},
	data() {
		return {
			operationInProgress: false,
			form: {
				optionalOutputKey: this.integration.nodeSettings.optionalOutputKey || "",
				doNotCombineInputAndOutputData: this.integration.nodeSettings.doNotCombineInputAndOutputData || false,
				continueOnFail: this.integration.nodeSettings.continueOnFail,
				displayName: this.integration.displayName,
				description: this.integration.description,
				mode: this.integration.nodeSettings.mode || null,
			},
			formRules: {},
		};
	},
	methods: {
		...mapActions(["changesToNodeSettingsMETHOD"]),
		copy() {
			copyToClipboard(this.integration.id);
			this.$notify({
				title: "Success",
				message: "Integration id copied to clipboard",
				type: "success",
				position: "bottom-left",
			});
		},
		update() {
			this.$refs.form.validate(async (valid) => {
				if (valid) {
					this.operationInProgress = true;
					const form = {
						workflowId: this.workflow.id,
						versionId: this.workflow.versionId,
						workflowLastUpdated: this.workflow.updatedAt,
						nodeId: this.integration.id,
						description: this.form.description,
						displayName: this.form.displayName,
						nodeSettings: {
							optionalOutputKey: this.form.optionalOutputKey || "",
							doNotCombineInputAndOutputData: this.form.doNotCombineInputAndOutputData || false,
							continueOnFail: this.form.continueOnFail,
							canvasPosition: this.integration.nodeSettings.canvasPosition,
							mode: this.form.mode,
						},
					};
					const result = await this.httpHelper("RUNTIME", "PUT", "workflow/updateNodeInWorkflow", form);
					setTimeout(() => {
						this.operationInProgress = false;
						if (!result?.error) {
							this.changesToNodeSettingsMETHOD(this.integration.id + "_" + uuidv4());
							this.$emit("operationComplete");
							this.workflow.updatedAt = result.response.workflowUpdatedAt;
							this.integration.nodeSettings.optionalOutputKey = this.form.optionalOutputKey || "";
							this.integration.nodeSettings.doNotCombineInputAndOutputData =
								this.form.doNotCombineInputAndOutputData || false;
							this.integration.nodeSettings.continueOnFail = this.form.continueOnFail;
							this.integration.nodeSettings.mode = this.form.mode;
							this.integration.description = this.form.description;
							this.integration.displayName = this.form.displayName;
							this.$notify({
								title: "Success",
								message: "Integration settings updated",
								type: "success",
								position: "bottom-left",
							});
						} else {
							this.$notifyError(this, result.error);
						}
					}, 500);
				} else {
					return;
				}
			});
		},
	},
};
</script>

<style scoped>
.fieldHelper {
	font-size: 10px;
	word-break: break-word;
	margin: 0px;
	line-height: 20px;
	margin-top: 5px;
}
</style>
